import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import {
  Lightning, Gauge, Database, Clock, Users, ArrowsClockwise,
  ChartLine, Shield, CaretUp, CaretDown, Pulse, SealCheck
} from '@phosphor-icons/react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, Cell, CartesianGrid
} from 'recharts';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = n => new Intl.NumberFormat('en-IN').format(n || 0);

function MetricCard({ label, value, sub, icon: Icon, color, pulse, testId }) {
  return (
    <div className="bg-white border border-[#e5e5ea] rounded-xl p-5 relative overflow-hidden" data-testid={testId}>
      {pulse && <div className="absolute top-3 right-3 w-2.5 h-2.5 rounded-full animate-pulse" style={{ backgroundColor: color }} />}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#9c9db7]">{label}</p>
          <p className="text-2xl font-black text-[#0A0A0A] mt-1">{value}</p>
          {sub && <p className="text-[11px] text-[#5a5c87] mt-0.5">{sub}</p>}
        </div>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}12` }}>
          <Icon size={20} style={{ color }} weight="bold" />
        </div>
      </div>
    </div>
  );
}

function LatencyTable({ latency }) {
  const rows = Object.entries(latency || {}).map(([endpoint, stats]) => ({
    endpoint: endpoint.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    key: endpoint,
    ...stats
  })).sort((a, b) => b.count - a.count);

  if (!rows.length) {
    return (
      <div className="bg-white border border-[#e5e5ea] rounded-xl p-5">
        <h3 className="text-sm font-bold text-[#0A0A0A] mb-3">API Latency Percentiles</h3>
        <p className="text-xs text-[#9c9db7]">No data yet. Make some API calls to start tracking.</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#e5e5ea] rounded-xl p-5" data-testid="latency-table">
      <h3 className="text-sm font-bold text-[#0A0A0A] mb-1">API Latency Percentiles</h3>
      <p className="text-[10px] text-[#9c9db7] mb-3">Response times in milliseconds</p>
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b-2 border-[#e5e5ea]">
              <th className="text-[10px] font-bold uppercase tracking-wider text-[#9c9db7] pb-2 pr-4">Endpoint</th>
              <th className="text-[10px] font-bold uppercase tracking-wider text-[#9c9db7] pb-2 px-3 text-right">P50</th>
              <th className="text-[10px] font-bold uppercase tracking-wider text-[#9c9db7] pb-2 px-3 text-right">P90</th>
              <th className="text-[10px] font-bold uppercase tracking-wider text-[#9c9db7] pb-2 px-3 text-right">P99</th>
              <th className="text-[10px] font-bold uppercase tracking-wider text-[#9c9db7] pb-2 px-3 text-right">Avg</th>
              <th className="text-[10px] font-bold uppercase tracking-wider text-[#9c9db7] pb-2 pl-3 text-right">Calls</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const p99Color = r.p99 > 5000 ? '#ea4b5a' : r.p99 > 1000 ? '#f6a609' : '#28cd8c';
              return (
                <tr key={r.key} className="border-b border-[#f0f0f5] hover:bg-[#fcfcfe] transition-colors">
                  <td className="text-xs font-semibold text-[#0A0A0A] py-2.5 pr-4">{r.endpoint}</td>
                  <td className="text-xs font-mono text-[#5a5c87] py-2.5 px-3 text-right">{r.p50}ms</td>
                  <td className="text-xs font-mono text-[#5a5c87] py-2.5 px-3 text-right">{r.p90}ms</td>
                  <td className="text-xs font-mono font-bold py-2.5 px-3 text-right" style={{ color: p99Color }}>{r.p99}ms</td>
                  <td className="text-xs font-mono text-[#5a5c87] py-2.5 px-3 text-right">{r.avg}ms</td>
                  <td className="text-xs font-semibold text-[#7d64ff] py-2.5 pl-3 text-right">{fmt(r.count)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RateLimitConfig({ config }) {
  if (!config) return null;
  const tiers = Object.entries(config.tiers || {});
  return (
    <div className="bg-white border border-[#e5e5ea] rounded-xl p-5" data-testid="rate-limit-config">
      <div className="flex items-center gap-2 mb-3">
        <Shield size={16} className="text-[#7d64ff]" weight="bold" />
        <h3 className="text-sm font-bold text-[#0A0A0A]">Rate Limit Configuration</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {tiers.map(([name, t]) => (
          <div key={name} className="border border-[#e5e5ea] rounded-lg p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#9c9db7] mb-1">{name}</p>
            <p className="text-lg font-black text-[#0A0A0A]">{t.max_requests} <span className="text-xs font-normal text-[#9c9db7]">req / {t.window_seconds}s</span></p>
          </div>
        ))}
      </div>
      <div className="mt-3">
        <p className="text-[10px] font-bold uppercase text-[#9c9db7] mb-1">Heavy Endpoints (10 req/min)</p>
        <div className="flex flex-wrap gap-1">
          {(config.heavy_endpoints || []).map(ep => (
            <span key={ep} className="text-[10px] bg-[#f8f7ff] text-[#7d64ff] px-2 py-0.5 rounded font-mono">{ep}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function OpsDashboardPage() {
  const [metrics, setMetrics] = useState(null);
  const [rlConfig, setRlConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchMetrics = useCallback(async () => {
    try {
      const [metricsRes, rlRes] = await Promise.all([
        axios.get(`${API}/api/admin/ops/metrics`, { withCredentials: true }),
        rlConfig ? Promise.resolve({ data: rlConfig }) : axios.get(`${API}/api/admin/ops/rate-limits`, { withCredentials: true }),
      ]);
      setMetrics(metricsRes.data);
      if (!rlConfig) setRlConfig(rlRes.data);
    } catch {}
    finally { setLoading(false); }
  }, [rlConfig]);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchMetrics, 10000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchMetrics]);

  const m = metrics || {};
  const cacheColor = (m.cache_hit_ratio || 0) >= 80 ? '#28cd8c' : (m.cache_hit_ratio || 0) >= 40 ? '#f6a609' : '#ea4b5a';

  return (
    <DashboardLayout title="Ops Dashboard" subtitle="Real-time API performance, rate limits & cache monitoring">
      {loading ? (
        <div className="loading-spinner"><div className="spinner"></div></div>
      ) : (
        <div className="space-y-6">
          {/* Controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Pulse size={16} className="text-[#28cd8c]" weight="bold" />
              <span className="text-xs font-semibold text-[#5a5c87]">
                {autoRefresh ? 'Live — refreshing every 10s' : 'Paused'}
              </span>
              {autoRefresh && <div className="w-2 h-2 rounded-full bg-[#28cd8c] animate-pulse" />}
            </div>
            <button
              onClick={() => setAutoRefresh(p => !p)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${autoRefresh ? 'border-[#ea4b5a] text-[#ea4b5a] hover:bg-[#ea4b5a]/5' : 'border-[#28cd8c] text-[#28cd8c] hover:bg-[#28cd8c]/5'}`}
              data-testid="toggle-auto-refresh"
            >
              {autoRefresh ? 'Pause' : 'Resume'}
            </button>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard label="Requests / Min" value={fmt(m.current_rpm)} icon={Lightning} color="#7d64ff" pulse testId="ops-rpm" />
            <MetricCard label="Cache Hit Ratio" value={`${m.cache_hit_ratio || 0}%`} icon={Database} color={cacheColor} testId="ops-cache-ratio" />
            <MetricCard label="Total Requests (6h)" value={fmt(m.total_requests_6h)} icon={ChartLine} color="#3b82f6" testId="ops-total-requests" />
            <MetricCard label="Redis" value={m.redis_status === 'connected' ? 'Connected' : 'Down'} icon={SealCheck} color={m.redis_status === 'connected' ? '#28cd8c' : '#ea4b5a'} testId="ops-redis-status" />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Throughput Chart */}
            <div className="bg-white border border-[#e5e5ea] rounded-xl p-5" data-testid="throughput-chart">
              <h3 className="text-sm font-bold text-[#0A0A0A] mb-1">Request Throughput</h3>
              <p className="text-[10px] text-[#9c9db7] mb-3">Requests per minute — last 60 minutes</p>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={(m.throughput || []).filter((_, i) => i % 2 === 0)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f5" />
                  <XAxis dataKey="time" tick={{ fontSize: 9, fill: '#9c9db7' }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 9, fill: '#9c9db7' }} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e5e5ea' }} />
                  <Area type="monotone" dataKey="requests" stroke="#7d64ff" fill="#7d64ff" fillOpacity={0.15} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Cache Hit/Miss Chart */}
            <div className="bg-white border border-[#e5e5ea] rounded-xl p-5" data-testid="cache-chart">
              <h3 className="text-sm font-bold text-[#0A0A0A] mb-1">Cache Performance</h3>
              <p className="text-[10px] text-[#9c9db7] mb-3">Hits vs misses — last 6 hours</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={m.cache_stats || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f5" />
                  <XAxis dataKey="hour" tick={{ fontSize: 9, fill: '#9c9db7' }} />
                  <YAxis tick={{ fontSize: 9, fill: '#9c9db7' }} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e5e5ea' }} />
                  <Bar dataKey="hits" stackId="a" fill="#28cd8c" radius={[0, 0, 0, 0]} name="Cache Hits" />
                  <Bar dataKey="misses" stackId="a" fill="#ea4b5a" radius={[4, 4, 0, 0]} name="Cache Misses" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Active Users + Top Endpoints */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Active Users */}
            <div className="bg-white border border-[#e5e5ea] rounded-xl p-5" data-testid="active-users-chart">
              <h3 className="text-sm font-bold text-[#0A0A0A] mb-1">Active Users</h3>
              <p className="text-[10px] text-[#9c9db7] mb-3">Unique users per minute — last 10 minutes</p>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={m.active_users || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f5" />
                  <XAxis dataKey="time" tick={{ fontSize: 9, fill: '#9c9db7' }} />
                  <YAxis tick={{ fontSize: 9, fill: '#9c9db7' }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e5e5ea' }} />
                  <Bar dataKey="users" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={24} name="Users" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Top Endpoints */}
            <div className="bg-white border border-[#e5e5ea] rounded-xl p-5" data-testid="top-endpoints">
              <h3 className="text-sm font-bold text-[#0A0A0A] mb-1">Top Endpoints (This Hour)</h3>
              <p className="text-[10px] text-[#9c9db7] mb-3">Most called API endpoints</p>
              {(m.top_endpoints || []).length === 0 ? (
                <p className="text-xs text-[#9c9db7] py-6 text-center">No data yet</p>
              ) : (
                <div className="space-y-2">
                  {(m.top_endpoints || []).slice(0, 8).map((ep, i) => {
                    const maxCount = m.top_endpoints[0]?.count || 1;
                    return (
                      <div key={ep.endpoint} className="flex items-center gap-3">
                        <span className="text-[10px] font-mono text-[#5a5c87] w-24 truncate">{ep.endpoint}</span>
                        <div className="flex-1 h-5 bg-[#f0f0f5] rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500" style={{
                            width: `${(ep.count / maxCount) * 100}%`,
                            backgroundColor: i === 0 ? '#7d64ff' : i < 3 ? '#3b82f6' : '#9c9db7'
                          }} />
                        </div>
                        <span className="text-xs font-bold text-[#0A0A0A] w-12 text-right">{fmt(ep.count)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Latency Table */}
          <LatencyTable latency={m.latency} />

          {/* Rate Limit Config */}
          <RateLimitConfig config={rlConfig} />

          {/* Connection Pools */}
          {m.connection_pools && Object.keys(m.connection_pools).length > 0 && (
            <div className="bg-white border border-[#e5e5ea] rounded-xl p-5" data-testid="connection-pools">
              <div className="flex items-center gap-2 mb-3">
                <Database size={16} className="text-[#3b82f6]" weight="bold" />
                <h3 className="text-sm font-bold text-[#0A0A0A]">MongoDB Connection Pools</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(m.connection_pools).map(([name, pool]) => (
                  <div key={name} className="border border-[#e5e5ea] rounded-lg p-4">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#9c9db7] mb-2">{name.replace('_', ' ')}</p>
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="text-2xl font-black text-[#0A0A0A]">{pool.max_pool_size}</span>
                      <span className="text-xs text-[#9c9db7]">max connections</span>
                    </div>
                    <p className="text-[11px] text-[#5a5c87]">Min: {pool.min_pool_size} warm connections</p>
                    {pool.read_preference && <p className="text-[10px] font-mono text-[#7d64ff] mt-1">readPreference: {pool.read_preference}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
